// Lazy export layer for the Completion Receipt.
//
// IMPORTANT: this module must NOT statically import html2canvas-pro / jspdf — they
// are heavy and are pulled in via dynamic import() inside the functions below so
// they land in a separate lazy chunk, never the main bundle. Only the call sites
// (Download / Save image / Share) trigger the import, on click.

const A4 = { width: 210, height: 297 }; // mm, portrait
const MARGIN = 10; // mm

/** Filesystem-safe receipt filename. */
export const receiptFilename = (referenceId: string, ext: 'pdf' | 'png'): string => {
	const safe = (referenceId || 'submission').replace(/[^A-Za-z0-9_-]+/g, '-').replace(/^-+|-+$/g, '');
	return `iElect-Certificate-${safe || 'submission'}.${ext}`;
};

/** Fetch a (possibly cross-origin) image and inline it as a data-URL — keeps the
 * html2canvas capture taint-free. Resolves null on any failure (CORS/offline/404)
 * so the certificate can render a placeholder instead of breaking the export. */
export const urlToDataUrl = async (url: string): Promise<string | null> => {
	try {
		const res = await fetch(url, { mode: 'cors' });
		if (!res.ok) return null;
		const blob = await res.blob();
		return await new Promise((resolve) => {
			const reader = new FileReader();
			reader.onloadend = () => resolve(typeof reader.result === 'string' ? reader.result : null);
			reader.onerror = () => resolve(null);
			reader.readAsDataURL(blob);
		});
	} catch {
		return null;
	}
};

/** Rasterise a DOM node to a high-DPI canvas (light background, fixed scale). */
const captureCanvas = async (node: HTMLElement): Promise<HTMLCanvasElement> => {
	const { default: html2canvas } = await import('html2canvas-pro');
	return html2canvas(node, {
		scale: Math.min(window.devicePixelRatio || 1, 2) * 1.5,
		backgroundColor: '#ffffff',
		useCORS: true,
		logging: false,
		windowWidth: node.scrollWidth,
	});
};

const canvasToBlob = (canvas: HTMLCanvasElement, type: string, quality?: number): Promise<Blob> =>
	new Promise((resolve, reject) => {
		canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('Canvas is empty'))), type, quality);
	});

/** PNG of the certificate (WhatsApp-friendly). */
export const exportPng = async (node: HTMLElement): Promise<Blob> => {
	const canvas = await captureCanvas(node);
	return canvasToBlob(canvas, 'image/png');
};

/** A4 PDF of the certificate, split across pages when taller than one page. */
export const exportPdf = async (node: HTMLElement): Promise<Blob> => {
	const canvas = await captureCanvas(node);
	const { jsPDF } = await import('jspdf');
	const pdf = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait', compress: true });

	const imgW = A4.width - MARGIN * 2;
	const imgH = (canvas.height * imgW) / canvas.width;
	const pageH = A4.height - MARGIN * 2;
	const img = canvas.toDataURL('image/jpeg', 0.92);

	if (imgH <= pageH) {
		pdf.addImage(img, 'JPEG', MARGIN, MARGIN, imgW, imgH, undefined, 'FAST');
	} else {
		// Slice the tall capture across pages using negative Y offsets.
		let remaining = imgH;
		let offset = 0;
		while (remaining > 0) {
			pdf.addImage(img, 'JPEG', MARGIN, MARGIN - offset, imgW, imgH, undefined, 'FAST');
			remaining -= pageH;
			offset += pageH;
			if (remaining > 0) pdf.addPage();
		}
	}
	return pdf.output('blob');
};

/** Trigger a browser download of a blob. */
export const downloadBlob = (blob: Blob, filename: string): void => {
	const url = URL.createObjectURL(blob);
	const a = document.createElement('a');
	a.href = url;
	a.download = filename;
	document.body.appendChild(a);
	a.click();
	a.remove();
	setTimeout(() => URL.revokeObjectURL(url), 1000);
};

export type ShareOutcome = 'shared' | 'downloaded' | 'cancelled';

/** Native share with a file when supported; otherwise download as a fallback.
 * Returns what actually happened so the caller can toast appropriately. */
export const shareReceipt = async (blob: Blob, filename: string, title: string, text: string): Promise<ShareOutcome> => {
	const type = blob.type || 'application/octet-stream';
	const file = new File([blob], filename, { type });
	const nav = navigator as Navigator & { canShare?: (data?: ShareData) => boolean };

	if (nav.canShare?.({ files: [file] })) {
		try {
			await nav.share({ files: [file], title, text });
			return 'shared';
		} catch (err) {
			// User dismissed the native sheet — not an error.
			if (err instanceof DOMException && err.name === 'AbortError') return 'cancelled';
			// Any other share failure → fall through to download.
		}
	}
	downloadBlob(blob, filename);
	return 'downloaded';
};
